import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null); // { user, org, role, memberships }
  const [loading, setLoading] = useState(true);

  const applyAuth = (data) => {
    localStorage.setItem("rooflead_token", data.token);
    setSession({ user: data.user, org: data.org, role: data.role, memberships: data.memberships || [] });
  };

  const refresh = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    // /auth/me returns AuthResponse minus token — token stays same
    setSession({ user: data.user, org: data.org, role: data.role, memberships: data.memberships || [] });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("rooflead_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setSession({ user: r.data.user, org: r.data.org, role: r.data.role, memberships: r.data.memberships || [] }))
      .catch(() => localStorage.removeItem("rooflead_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    applyAuth(data);
    return data;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    applyAuth(data);
    return data;
  };

  const switchOrg = async (org_id) => {
    const { data } = await api.post("/me/switch-org", { org_id });
    applyAuth(data);
    return data;
  };

  const acceptInvite = async (token) => {
    const { data } = await api.post(`/invitations/${token}/accept`);
    applyAuth(data);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("rooflead_token");
    setSession(null);
  };

  const logoutAll = async () => {
    try {
      await api.post("/auth/logout-all");
    } finally {
      logout();
    }
  };

  const changePassword = async (current_password, new_password) => {
    const { data } = await api.post("/auth/change-password", { current_password, new_password });
    applyAuth(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{
      user: session?.user, org: session?.org, role: session?.role, memberships: session?.memberships || [],
      loading, login, signup, logout, logoutAll, changePassword, refresh, switchOrg, acceptInvite,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
